import React, { Fragment, useCallback, useState } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Spin, Typography } from 'antd';
import { useSelector } from 'react-redux';
import useRequest from '../../queries/useRequest';
import { requestColumns as columns } from './constants/requestColumns';
import { requestCardMutationFns } from '../../helpers/requestCardMutationFns';
import { CompleteModal, InvoiceManager } from '../requestComponents';
import { queryKeys } from '../../queries/queryKeys';
import AcceptanceRequestToCalendar from '../AcceptanceRequestToCalendar/AcceptanceRequestToCalendar';
import { getUpdatedRequest } from '../../helpers/requestUtils';
import styles from './RequestCard.module.scss';

const { Text } = Typography;
const { RequestInvoiceModalProvider } = InvoiceManager;

const onMutationSuccess = ({ requestId, newParams, queryClient }) => {
  queryClient.setQueryData(['request', requestId], (oldRequests) =>
    oldRequests.map((oldRequest) => {
      if (oldRequest.id === requestId) {
        if (newParams.priority?.id === 0) {
          const oldRequestCopy = { ...oldRequest };
          delete oldRequestCopy.priority;
          return oldRequestCopy;
        } else {
          return { ...oldRequest, ...newParams };
        }
      }
      return oldRequest;
    })
  );
};

const statusIds = {
  closed: 3,
  cancelled: 4,
};

const noEditableStatusIds = new Set([statusIds.closed, statusIds.cancelled]);

const useSetInvoiceSuccess = () => {
  const queryClient = useQueryClient();

  const onSuccess = useCallback(
    (updatedRequest) => {
      const requestId = updatedRequest.id
      queryClient.setQueryData(['request', requestId], (oldRequests) =>
        oldRequests.map((oldRequest) =>
          oldRequest.id === requestId ? updatedRequest : oldRequest
        )
      );
    },
    [queryClient]
  );

  return onSuccess;
};

const RequestCard = ({
  calendarIcon,
  onCalendarClick,
  afterAddToAcceptanceCalendar,
}) => {
  const [completeModalVisible, setCompleteModalVisible] = useState(false);

  const selectedRequestId = useSelector(
    (state) => state.requestCardStore.selectedRequestId
  );

  const queryClient = useQueryClient();

  const updateRequest = useCallback(
    (updatedRequest) => {
      queryClient.setQueryData(
        queryKeys.request(selectedRequestId),
        (oldRequests) =>
          oldRequests.map((oldRequest) =>
            oldRequest.id === updatedRequest.id ? updatedRequest : oldRequest
          )
      );
    },
    [queryClient, selectedRequestId]
  );

  const { data: requests, isFetching: isRequestsFetching } =
    useRequest(selectedRequestId);

  const handleSetInvoiceSuccess = useSetInvoiceSuccess();

  const requestStatusMutation = useMutation(requestCardMutationFns.status, {
    onSuccess: async (_data, { request }) => {
      const updatedRequest = await getUpdatedRequest(request.id);
      if (updatedRequest) {
        updateRequest(updatedRequest);
      }
    },
  });

  const requestCreationMethodMutation = useMutation(
    requestCardMutationFns.creationMethod,
    {
      onSuccess: (_data, { requestId, creationMethod }) =>
        onMutationSuccess({
          requestId,
          newParams: { creationMethod },
          queryClient,
        }),
    }
  );

  const requestPriorityMutation = useMutation(requestCardMutationFns.priority, {
    onSuccess: (_data, { requestId, priority }) =>
      onMutationSuccess({ requestId, newParams: { priority }, queryClient }),
  });

  const request = requests?.length ? requests[0] : null;
  const canChange = Boolean(
    request && !noEditableStatusIds.has(request.statusId)
  );

  const gridTemplateColumns = columns
    .map((column) => column.fraction)
    .join(' ');

  const handleStatusChange = (request, status) => {
    requestStatusMutation.mutate({ request, status });
  };

  const handleCreationMethodChange = (requestId, creationMethod) => {
    requestCreationMethodMutation.mutate({ requestId, creationMethod });
  };

  const handlePriorityChange = (requestId, priority) => {
    requestPriorityMutation.mutate({ requestId, priority });
  };

  const handleCompletedStatusClick = () => {
    setCompleteModalVisible(true);
  };

  const handleCompleteModalCancel = () => {
    setCompleteModalVisible(false);
  };

  const handleCompleteModalAfterSubmit = async ({
    updatedRequest,
    formValues,
  }) => {
    setCompleteModalVisible(false);

    if (updatedRequest) {
      updateRequest(updatedRequest);

      if (formValues.note.trim()) {
        queryClient.invalidateQueries(
          queryKeys.requestComments(selectedRequestId)
        );
      }

      if (formValues.files.length) {
        queryClient.invalidateQueries(
          queryKeys.requestAttachments(selectedRequestId)
        );
      }
    }
  };

  const handleAfterAcceptanceToCalendarSubmit = ({ updatedRequest }) => {
    if (updatedRequest) {
      queryClient.setQueryData(
        queryKeys.request(selectedRequestId),
        (oldRequests) =>
          oldRequests.map((oldRequest) =>
            oldRequest.id === updatedRequest.id ? updatedRequest : oldRequest
          )
      );
    }

    if (typeof afterAddToAcceptanceCalendar === 'function') {
      afterAddToAcceptanceCalendar();
    }
  };

  const handleBindCallSuccess = () => {
    queryClient.setQueryData(['request', selectedRequestId], (oldRequests) =>
      oldRequests.map((oldRequest) => {
        return oldRequest.id === selectedRequestId
          ? { ...oldRequest, hasRecord: true }
          : oldRequest;
      })
    );
  };

  return (
    <Spin spinning={isRequestsFetching || requestStatusMutation.isLoading}>
      <RequestInvoiceModalProvider onSuccess={handleSetInvoiceSuccess}>
        <div className={styles.root}>
          <div
            className={styles.header}
            style={{ gridTemplateColumns: gridTemplateColumns }}
          >
            {columns.map((columnData) => {
              const { key, column, title } = columnData;

              return (
                <div
                  key={key}
                  className={styles.headerCell}
                  style={{ gridColumn: column }}
                >
                  <Text className={styles.headerContent}>{title}</Text>
                </div>
              );
            })}
          </div>
          {request ? (
            <div
              className={styles.body}
              style={{ gridTemplateColumns: gridTemplateColumns }}
            >
              {columns.map((columnData) => {
                const {
                  key,
                  getCellStyles,
                  getExpandedCellStyles,
                  renderItem,
                  renderExpandedItem,
                } = columnData;

                return (
                  <Fragment key={key}>
                    {typeof renderItem === 'function' ? (
                      <div
                        className={styles.bodyCell}
                        style={getCellStyles ? getCellStyles() : {}}
                      >
                        {renderItem({
                          request,
                          onStatusChange: handleStatusChange,
                          onCompletedStatusClick: handleCompletedStatusClick,
                          onCreationMethodChange: canChange
                            ? handleCreationMethodChange
                            : undefined,
                          onPriorityChange: handlePriorityChange,
                          calendarIcon,
                          onCalendarClick,
                          onBindCallToRequestSucces: handleBindCallSuccess,
                          canChange,
                        })}
                      </div>
                    ) : null}
                    {typeof renderExpandedItem === 'function' ? (
                      <div
                        className={styles.bodyCell}
                        style={
                          getExpandedCellStyles ? getExpandedCellStyles() : {}
                        }
                      >
                        {renderExpandedItem({ request, canChange })}
                      </div>
                    ) : null}
                  </Fragment>
                );
              })}
            </div>
          ) : (
            <div className={styles.emptyBody} />
          )}
        </div>
        <CompleteModal
          visible={completeModalVisible}
          onCancel={handleCompleteModalCancel}
          afterSubmit={handleCompleteModalAfterSubmit}
          requestId={selectedRequestId}
        />
        <AcceptanceRequestToCalendar
          afterSubmit={handleAfterAcceptanceToCalendarSubmit}
        />
      </RequestInvoiceModalProvider>
    </Spin>
  );
};

export default RequestCard;
